import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import NavBar from './NavBar';
import { seoConfig, updateMetaTags } from '../utils/seoConfig';
import '../styles/CourseList.css';

const CourseList = () => {
    const navigate = useNavigate();
    const { t, language } = useLanguage();
    const [userInfo, setUserInfo] = useState(null);
    const [courses, setCourses] = useState([]);
    const [filter, setFilter] = useState('all');

    // 코스 데이터 (실제로는 API에서 가져옴)
    const allCourses = [
        {
            id: 'soccer-card-making',
            title: language === 'ko' ? '축구 카드 만들기' : 'Create Soccer Cards',
            description: language === 'ko' ? 'Python과 HTML/CSS로 나만의 축구 카드를 만들어요' : 'Create your own soccer cards with Python and HTML/CSS',
            type: 'creation',
            typeLabel: t('creation'),
            typeEmoji: '⭐',
            difficulty: 'beginner',
            steps: 15,
            estimatedTime: language === 'ko' ? '2시간' : '2 hours',
            tags: ['soccer', 'python', 'html'],
            preview: '/images/soccer-card-preview.png',
            enable:true
        },
        {
            id: 'soccer-card-collection',
            title: language === 'ko' ? '축구 카드 수집' : 'Soccer Card Collection',
            description: language === 'ko' ? '카드 수집 게임을 만들며 조건문과 확률을 배워요' : 'Learn conditionals and probability by making a card collection game',
            type: 'game',
            typeLabel: t('gameType'),
            typeEmoji: '🎮',
            difficulty: 'intermediate',
            steps: 10,
            estimatedTime: language === 'ko' ? '1.5시간' : '1.5 hours',
            tags: ['soccer', 'game', 'python'],
            preview: '/images/soccer-collection-preview.png',
            enable:false
        },
        {
            id: 'lotto-number',
            title: language === 'ko' ? '로또 번호 추천' : 'Lottery Number Generator',
            description: language === 'ko' ? '랜덤과 통계를 활용한 번호 생성기를 만들어요' : 'Create a number generator using random and statistics',
            type: 'analysis',
            typeLabel: t('dataType'),
            typeEmoji: '🎲',
            difficulty: 'beginner',
            steps: 12,
            estimatedTime: language === 'ko' ? '1.5시간' : '1.5 hours',
            tags: ['data', 'python', 'math'],
            preview: '/images/lotto-preview.png',
            enable:false
        },
        {
            id: 'pokemon-card-making',
            title: language === 'ko' ? '포켓몬 카드 만들기' : 'Create Pokemon Cards',
            description: language === 'ko' ? '나만의 포켓몬 카드를 디자인하고 만들어요' : 'Design and create your own Pokemon cards',
            type: 'creation',
            typeLabel: t('creation'),
            typeEmoji: '⭐',
            difficulty: 'beginner',
            steps: 15,
            estimatedTime: language === 'ko' ? '2시간' : '2 hours',
            tags: ['pokemon', 'python', 'html'],
            preview: '/images/pokemon-card-preview.png',
            enable:false
        }
    ];

    useEffect(() => {
        // SEO 메타 태그 업데이트
        updateMetaTags(seoConfig.courses);
        
        const savedUserInfo = localStorage.getItem('userInfo');
        if (!savedUserInfo) {
            navigate('/onboarding');
            return;
        }

        const info = JSON.parse(savedUserInfo);
        setUserInfo(info);

        // 사용자 관심사에 따른 코스 정렬
        const sortedCourses = [...allCourses].sort((a, b) => {
            const aScore = a.tags.filter(tag => info.interests.includes(tag)).length;
            const bScore = b.tags.filter(tag => info.interests.includes(tag)).length;
            return bScore - aScore;
        });

        setCourses(sortedCourses);
    }, [navigate]);

    const filteredCourses = filter === 'all'
        ? courses
        : courses.filter(course => course.type === filter);

    const getDifficultyLabel = (difficulty) => {
        return t(difficulty);
    };

    const getDifficultyColor = (difficulty) => {
        const colors = {
            beginner: '#4CAF50',
            intermediate: '#FF9800',
            advanced: '#F44336'
        };
        return colors[difficulty] || '#666';
    };

    return (
        <div className="course-list-container">
            <NavBar />
            
            {/* Header */}
            <header className="course-header">
                <div className="header-container">
                    <div className="welcome-section">
                        <h1 className="welcome-title">
                            {t('helloUser', { name: userInfo?.nickname })}
                        </h1>
                        <p className="welcome-subtitle">
                            <span className="hashtag-container">
                                {userInfo?.interests.map(interest => (
                                    <span key={interest} className="interest-tag">
                                        #{t(interest)}
                                    </span>
                                ))}
                            </span>
                            <span className="subtitle-text">{t('codingAdventure')}</span>
                        </p>
                    </div>
                </div>
            </header>

            {/* Filter Tabs */}
            <div className="filter-tabs">
                <button
                    className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
                    onClick={() => setFilter('all')}
                >
                    {t('all')}
                </button>
                <button
                    className={`filter-tab ${filter === 'creation' ? 'active' : ''}`}
                    onClick={() => setFilter('creation')}
                >
                    ⭐ {t('creation')}
                </button>
                <button
                    className={`filter-tab ${filter === 'game' ? 'active' : ''}`}
                    onClick={() => setFilter('game')}
                >
                    🎮 {t('gameType')}
                </button>
                <button
                    className={`filter-tab ${filter === 'analysis' ? 'active' : ''}`}
                    onClick={() => setFilter('analysis')}
                >
                    🎲 {t('dataType')}
                </button>
            </div>

            {/* Course Grid */}
            <div className="course-grid">
                {filteredCourses.map(course => (
                    <div
                        key={course.id}
                        className={`course-card ${!course.enable ? 'disabled' : ''}`}
                        onClick={() => course.enable && navigate(`/courses/${course.id}`)}
                    >
                        <div className="course-preview">
                            <img 
                                src={course.preview} 
                                alt={course.title} 
                                onError={(e) => {
                                    e.target.src = '/images/noimage.png';
                                }}
                            />
                            <div className="course-type-badge">
                                <span className="type-emoji">{course.typeEmoji}</span>
                                <span className="type-label">{course.typeLabel}</span>
                            </div>
                            {!course.enable && (
                                <div className="course-locked-overlay">
                                    <span className="lock-icon">🔒</span>
                                    <span className="lock-text">{t('comingSoon')}</span>
                                </div>
                            )}
                        </div>

                        <div className="course-content">
                            <h3 className="course-title">{course.title}</h3>
                            <p className="course-description">{course.description}</p>

                            <div className="course-meta">
                <span
                    className="difficulty-badge"
                    style={{ backgroundColor: getDifficultyColor(course.difficulty) }}
                >
                  {getDifficultyLabel(course.difficulty)}
                </span>
                                <span className="course-steps">{course.steps}{t('steps')}</span>
                                <span className="course-time">⏱️ {course.estimatedTime}</span>
                            </div>

                            {/* 사용자 관심사와 매칭되는 태그 표시 */}
                            {course.tags.some(tag => userInfo?.interests.includes(tag)) && (
                                <div className="matching-interests">
                                    <span className="match-label">{t('matchingInterests')}</span>
                                </div>
                            )}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default CourseList;